import { fromJS, OrderedSet, Set as ImmutableSet } from 'immutable';
import { useMemo, useState, useEffect } from 'react';
import I18n from 'I18n';
import { Button } from 'foundations-components/transitional/components/Button';
import { H7 } from 'foundations-components/transitional/components/H7';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';

// @ts-expect-error untyped import
import FilterOperatorBoolInput from 'customer-data-filters/components/operator/FilterOperatorBoolInput';
// @ts-expect-error untyped import
import FilterOperatorDateInput from 'customer-data-filters/components/operator/FilterOperatorDateInput';
// @ts-expect-error untyped import
import FilterOperatorMultiStringInput from 'customer-data-filters/components/operator/FilterOperatorMultiStringInput';
// @ts-expect-error untyped import
import FilterOperatorNumberInput from 'customer-data-filters/components/operator/FilterOperatorNumberInput';
// @ts-expect-error untyped import
import FilterOperatorRefinableInput from 'customer-data-filters/components/operator/FilterOperatorRefinableInput';
// @ts-expect-error untyped import
import FilterOperatorTextInput from 'customer-data-filters/components/operator/FilterOperatorTextInput';
import { fromObjectSegFilterStrict } from 'customer-data-filters/converters/objectSeg/fromObjectSegFilter';
import { getObjectSegOperatorsForTypeStrict } from 'customer-data-filters/converters/objectSeg/ObjectSegTypeToOperator';
import { toObjectSegFilterStrict } from 'customer-data-filters/converters/objectSeg/toObjectSegFilter';
import * as DisplayTypes from 'customer-data-filters/filterQueryFormat/DisplayTypes';
import DSFieldRecord from 'customer-data-filters/filterQueryFormat/DSFieldRecord/DSFieldRecord';
import FilterOperatorErrorRecord from 'customer-data-filters/filterQueryFormat/FilterOperatorErrorRecord';
// @ts-expect-error untyped import
import Any from 'customer-data-filters/filterQueryFormat/operator/Any';
import { EverContained, EverEqual, EverEqualAny, NeverContained, NeverEqual, NeverEqualAny, NotUpdatedInLastXDays, UpdatedAfter, UpdatedBefore, UpdatedInLastXDays
// @ts-expect-error untyped import
} from 'customer-data-filters/filterQueryFormat/operator/Operators';
import { REPORTING_LEGACY_OPERATOR_USAGE } from 'reporting-data/constants/trackingMetrics';
import { toPropertyBasicType } from '../../../../../dataset/type-utils';
import { validateFilterDateRangeValue, validateFilterOperatorValue
// @ts-expect-error untyped import
} from '../../filter-validation-utils';
// @ts-expect-error untyped import
import { PropertyFilterPanel } from '../property-filter';
import { Field } from '../../../../schema/column-records';
import { DATASET_TABLE } from '../../../../schema/table-records';
// @ts-expect-error untyped import
import { CloneFilterSelect } from '../shared/CloneFilterSelect';
// @ts-expect-error untyped import
import { GroupFilterControl } from '../shared/GroupFilterControl';
import { getIsUngated, useFilterRelevantUserInfo } from '../../../../../shared/lib/utils';
// @ts-expect-error untyped import
import { useField } from '../SourceMeta';
import { buildSpecialOptionsByReferenceType, getOperatorLabel, SNOWFLAKE_FILTER_FAMILY, toObjectSegAny
// @ts-expect-error untyped import
} from '../../xo-filter-utils';
import { Metrics } from '../../../../../shared/Metrics';
import { NewPropertyFilterPanel } from '../property-filter/NewPropertyFilterPanel';
import { NewFieldFilterPanel } from './NewFieldFilterPanel';
import { Expression } from '../../../../../dataset/expression-records';
import { getIsUngatedToSnowflakePropertyFilterMigration } from '../../../../utils/filter-utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PROPERTY_HISTORY_OPERATORS = ImmutableSet.of(EverContained, EverEqual, EverEqualAny, NeverContained, NeverEqual, NeverEqualAny, NotUpdatedInLastXDays, UpdatedAfter, UpdatedBefore, UpdatedInLastXDays);
const NO_ERROR = FilterOperatorErrorRecord({
  error: false
});
const FieldFilterPanel = ({
  filter,
  groupId,
  onDone,
  onCancel,
  onCloneFilter
}) => {
  const fieldName = filter.field.name;
  const datasetField = useField(fieldName);
  const datasetFieldBasicType = toPropertyBasicType(datasetField.type);
  const [draftFilter, setDraft] = useState(filter);
  const [draftGroupId, setGroup] = useState(groupId);
  const {
    userId,
    userGates
  } = useFilterRelevantUserInfo();
  const isUngatedToSnowflakePropertyFilterMigration = getIsUngatedToSnowflakePropertyFilterMigration(userGates);
  const mappedProps = useMemo(() => {
    if (!datasetField.derived) {
      return undefined;
    }
    const {
      filter: fieldFilter
    } = draftFilter;
    const dsField = DSFieldRecord({
      name: fieldName,
      label: datasetField.label,
      type: datasetFieldBasicType
    });
    const baseOperators = getObjectSegOperatorsForTypeStrict(datasetFieldBasicType, SNOWFLAKE_FILTER_FAMILY, gate => getIsUngated(gate, userGates), true);
    const operators = OrderedSet().withMutations(set => {
      if (!baseOperators) {
        return;
      }
      baseOperators.forEach(operator => {
        if (!PROPERTY_HISTORY_OPERATORS.includes(operator)) {
          set.add(operator);
        }
      });
    });
    const maybeOperator = fieldFilter && fieldFilter.get('operation') && fromObjectSegFilterStrict(fieldFilter.toJS(), () => dsField, SNOWFLAKE_FILTER_FAMILY, gate => getIsUngated(gate, userGates), true);
    const operator = maybeOperator && operators.map(o => o._name).includes(maybeOperator.name) ? maybeOperator : Any.of(dsField);
    const getReferencedObjectType = () => null;
    const getFamilyValueResolver = () => null;
    const getInputComponent = () => {
      const fieldType = operator.field.displayType || operator.field.type;
      if (fieldType === DisplayTypes.StringDisplayType && operator.constructor.isIterableField('value')) {
        return FilterOperatorMultiStringInput;
      }
      if (datasetFieldBasicType === 'bool') {
        return FilterOperatorBoolInput;
      }
      if (datasetFieldBasicType === 'datetime' || datasetFieldBasicType === 'date') {
        return FilterOperatorDateInput;
      }
      if (datasetFieldBasicType === 'number') {
        return FilterOperatorNumberInput;
      }
      return FilterOperatorTextInput;
    };
    return {
      currencyCode: 'USD',
      error: NO_ERROR,
      showIncludeUnknownValues: false,
      isFiscalYearEnabled: false,
      isRollingDateOffsetInputEnabled: false,
      isXoEnabled: true,
      filterFamily: DATASET_TABLE,
      getIsUngated: gate => getIsUngated(gate, userGates),
      getDefaultTimezone: () => I18n.moment.portalTz().tz(),
      getFilterFamilyObjectName: () => '',
      getObjectPropertyLabel: () => '',
      getOperatorLabel,
      getReferencedObjectType,
      getSpecialOptionsForReferenceType: buildSpecialOptionsByReferenceType(datasetFieldBasicType, userId),
      getFamilyValueResolver,
      getInputComponent,
      getLabelString: () => datasetField.label,
      getPlaceholder: () => '',
      getOperators: () => operators,
      value: operator
    };
  }, [draftFilter, fieldName, datasetField, datasetFieldBasicType, userId, userGates]);
  useEffect(() => {
    if (!isUngatedToSnowflakePropertyFilterMigration) {
      Metrics.counter(REPORTING_LEGACY_OPERATOR_USAGE, {
        propertyType: 'field-filter'
      }).increment();
    }
  }, [isUngatedToSnowflakePropertyFilterMigration]);
  if (!datasetField.derived) {
    const expression = Expression(datasetField.expression);
    const propertyFilter = filter.set('field', Field({
      name: expression.name,
      table: expression.table,
      type: datasetField.type.propertyType
    }));
    const handleCloneFieldFilter = ({
      groupIndex
    }) => {
      onCloneFilter({
        draftFilter,
        groupId: groupIndex
      });
    };
    const PropertyFilter = isUngatedToSnowflakePropertyFilterMigration ? NewPropertyFilterPanel : PropertyFilterPanel;
    return /*#__PURE__*/_jsx(PropertyFilter, {
      filter: propertyFilter,
      groupId: groupId,
      onDone: ({
        draftFilter: nextFilter,
        draftGroupId: nextGroupId
      }) => onDone({
        draftFilter: nextFilter.set('field', filter.get('field')),
        draftGroupId: nextGroupId
      }),
      onCancel: onCancel,
      onCloneFilter: handleCloneFieldFilter
    });
  }
  if (isUngatedToSnowflakePropertyFilterMigration) {
    return /*#__PURE__*/_jsx(NewFieldFilterPanel, {
      filter: filter,
      groupId: groupId,
      onDone: onDone,
      onCancel: onCancel
    });
  }
  const handleChange = ({
    target: {
      value: operator
    }
  }) => {
    const nextObjectSegFilter = operator instanceof Any ? toObjectSegAny(operator) : toObjectSegFilterStrict(operator, SNOWFLAKE_FILTER_FAMILY, gate => getIsUngated(gate, userGates), true);
    const nextFilter = filter.setIn(['field', 'type'], datasetFieldBasicType).set('filter', fromJS(nextObjectSegFilter));
    setDraft(nextFilter);
  };
  const handleDone = () => {
    const operator = mappedProps && mappedProps.value;
    const nextObjectSegFilter = operator instanceof Any ? toObjectSegAny(operator) : toObjectSegFilterStrict(operator, SNOWFLAKE_FILTER_FAMILY, gate => getIsUngated(gate, userGates), true);
    const nextFilter = filter.setIn(['field', 'type'], datasetFieldBasicType).set('filter', fromJS(nextObjectSegFilter));
    onDone({
      draftFilter: nextFilter,
      draftGroupId
    });
  };
  const handleCloneFilter = groupIndex => {
    onCloneFilter({
      draftFilter,
      groupId: groupIndex
    });
  };
  const isValidFilter = validateFilterOperatorValue(draftFilter, userGates);
  const isValidDateRangeValue = validateFilterDateRangeValue(draftFilter);
  const isSubmitDisabled = !isValidFilter || !isValidDateRangeValue;
  const disabledButtonTooltipCopy = isSubmitDisabled && !isValidDateRangeValue ? I18n.text('reporting-snowflake.filter-editor.editor-panel.invalid-date-filter') : I18n.text('reporting-snowflake.filter-editor.editor-panel.incomplete-filter-options');
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "filter-edit-panel",
    children: [/*#__PURE__*/_jsx(H7, {
      children: datasetField.label
    }), /*#__PURE__*/_jsx(FilterOperatorRefinableInput, Object.assign({}, mappedProps, {
      onChange: handleChange
    })), /*#__PURE__*/_jsx(GroupFilterControl, {
      currentGroupId: draftGroupId,
      onGroupSelected: nextGroupId => setGroup(nextGroupId)
    }), /*#__PURE__*/_jsxs(TFlex, {
      justify: "between",
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(Tooltip, {
          title: disabledButtonTooltipCopy,
          placement: "top right",
          disabled: !isSubmitDisabled,
          children: /*#__PURE__*/_jsx(Button, {
            use: "tertiary",
            size: "xs",
            "data-test-id": "filter-editor-panel-apply-btn",
            disabled: isSubmitDisabled,
            onClick: handleDone,
            children: I18n.text('reporting-snowflake.filter-editor.editor-panel.apply')
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          size: "xs",
          onClick: onCancel,
          children: I18n.text('reporting-snowflake.filter-editor.editor-panel.cancel')
        })]
      }), /*#__PURE__*/_jsx(CloneFilterSelect, {
        currentGroupId: groupId,
        disabled: isSubmitDisabled,
        tooltipMessage: disabledButtonTooltipCopy,
        onCloneFilter: handleCloneFilter
      })]
    })]
  });
};
export default FieldFilterPanel;